/**
 * Shifts target elements in the array to a specified direction. Returns a new array.
 * Each element in the array should be unique
 *
 * See shift.spec.ts for examples.
 *
 * @param {Array} array - The original array from which elements are shifted.
 *                        Elements should be unique.
 * @param {Array} targets - The target elements to shift in the array.
 *                          Each element should exist in the array.
 * @param {string} direction - The direction to shift the elements.
 *                             Should be one of 'left', 'right', 'first', 'last'.
 *
 * @returns {Array} The new array with shifted elements.
 */
export function shift<T>(array: Array<T>, targets: Array<T>, direction: 'left' | 'right' | 'first' | 'last') {
  const arrayCopy = [...array];
  // In case there are targets that dont exist in the array - remove them
  const filteredTargets = targets.filter((target) => arrayCopy.includes(target));

  // if no targets are in the array, return the original array
  if (!filteredTargets.length) return arrayCopy;

  // sort targets after their idx in the array
  const sortedTargets = [...filteredTargets].sort((a, b) => arrayCopy.indexOf(a) - arrayCopy.indexOf(b));

  // Find the highest and smallest index of the targets
  let maxTargetIdx = 0;
  let minTargetIdx = arrayCopy.length - 1;

  sortedTargets.forEach((target) => {
    const index = arrayCopy.indexOf(target);

    if (index > maxTargetIdx) maxTargetIdx = index;
    if (index < minTargetIdx) minTargetIdx = index;
  });

  // Calculate the index where the targets will be inserted
  let insertIdx = 0;
  switch (direction) {
    case 'left':
      insertIdx = minTargetIdx - 1;
      break;
    case 'right':
      insertIdx = maxTargetIdx + 1;
      break;
  }

  // cant insert before the first index
  if (insertIdx < 0) insertIdx = 0;
  // cant insert after the last index
  if (insertIdx > arrayCopy.length) insertIdx = arrayCopy.length;

  const isLast = direction === 'last' || insertIdx === arrayCopy.length - 1 || insertIdx === arrayCopy.length;
  const isFirst = direction === 'first' || insertIdx === 0;

  // remove the targets from the array
  const filteredArray = arrayCopy.filter((targets) => !sortedTargets.includes(targets));

  // if it's the last, just add it to the end
  if (isLast) return [...filteredArray, ...sortedTargets];
  // if it's the first, just add it to the beginning
  if (isFirst) return [...sortedTargets, ...filteredArray];

  // check the what the value is at the insert index
  // insert the targets before or after this value
  // this should be ok since each value in the array is unique
  const elementAtInsertIdx = arrayCopy[insertIdx];
  const elementAtInsertIndexIndex = filteredArray.indexOf(elementAtInsertIdx);
  const insertAtIdx = direction === 'right' ? elementAtInsertIndexIndex + 1 : elementAtInsertIndexIndex;
  filteredArray.splice(insertAtIdx, 0, ...sortedTargets);

  return filteredArray;
}
